/**
 * API client for the reference-data catalog.
 * Backend issue: opentrms/opentrms issue 477.
 *
 * Endpoints:
 *   GET /api/v1/ref/manifest         →  RefManifest
 *   GET /api/v1/ref/:name            →  RefCatalog   (honors If-None-Match / 304)
 *
 * The module keeps an in-memory ETag cache so that repeated calls for the same
 * catalog within a session avoid re-parsing a 200 response they already have.
 *
 * Force-refresh path: pass `forceRefresh: true` in `RefDataOpts` to skip the
 * If-None-Match header and add Cache-Control: no-cache, bypassing HTTP caches.
 */
import type { ApiOpts } from './client';
import { refCatalogSchema, refManifestSchema } from '@/schemas/refData';
import type { RefCatalog, RefManifest } from '@/schemas/refData';
import { generateCorrelationId } from '@/shared/correlation';

// ── Typed fetch helpers ───────────────────────────────────────────────────────

// Re-use the token accessor registered by AuthProvider without a circular dep.
// We replicate only the headers we need rather than calling apiClient so that
// we can handle the 304 branch (apiClient throws on non-ok responses).
type RefDataOpts = ApiOpts & {
  /** Skip If-None-Match and add Cache-Control: no-cache. */
  forceRefresh?: boolean;
};

let _getToken: (() => string | null) | null = null;

/** Called by the same `configureApiClient` wiring — or separately if needed. */
export function configureRefDataClient(getToken: () => string | null): void {
  _getToken = getToken;
}

function resolveUrl(path: string): string {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (typeof base === 'string' && base.trim() !== '') {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    return `${normalizedBase}${path}`;
  }
  return path;
}

function baseHeaders(correlationId: string): Record<string, string> {
  const token = _getToken?.();
  const h: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-ID': correlationId,
  };
  if (token) {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
}

// ── Schema mismatch error ─────────────────────────────────────────────────────

export class RefDataSchemaError extends Error {
  constructor(catalog: string, cause: unknown) {
    super(`RefData schema mismatch for "${catalog}"`);
    this.name = 'RefDataSchemaError';
    this.cause = cause;
  }
}

// ── In-memory ETag cache ──────────────────────────────────────────────────────

type CacheEntry = { etag: string; body: RefCatalog };

const _etagCache = new Map<string, CacheEntry>();

/** Exposed for tests — clears the in-memory ETag cache between cases. */
export function _clearEtagCache(): void {
  _etagCache.clear();
}

/**
 * Clears all entries from the in-memory ETag cache.
 * Call before invalidating queries to ensure the next fetch sends no
 * If-None-Match header and receives a full 200 response instead of 304.
 */
export function clearEtagCache(): void {
  _etagCache.clear();
}

/**
 * Clears a single catalog's entry from the in-memory ETag cache.
 * Use when only one catalog needs force-refreshing.
 */
export function clearEtagCacheFor(name: string): void {
  _etagCache.delete(name);
}

// ── fetchManifest ─────────────────────────────────────────────────────────────

export async function fetchManifest(opts?: RefDataOpts): Promise<RefManifest> {
  const correlationId = opts?.correlationId ?? generateCorrelationId();
  const headers = baseHeaders(correlationId);

  const res = await fetch(resolveUrl('/api/v1/ref/manifest'), {
    method: 'GET',
    headers,
    signal: opts?.signal,
  });

  if (!res.ok) {
    throw new Error(`fetchManifest: HTTP ${res.status}`);
  }

  const raw: unknown = await res.json();
  const parsed = refManifestSchema.safeParse(raw);
  if (!parsed.success) {
    throw new RefDataSchemaError('manifest', parsed.error);
  }
  return parsed.data;
}

// ── fetchCatalog ──────────────────────────────────────────────────────────────

export async function fetchCatalog(
  name: string,
  signal?: AbortSignal,
  opts?: RefDataOpts,
): Promise<RefCatalog> {
  const correlationId = opts?.correlationId ?? generateCorrelationId();
  const headers = baseHeaders(correlationId);

  const cached = _etagCache.get(name);
  const forceRefresh = opts?.forceRefresh === true;

  if (cached && !forceRefresh) {
    headers['If-None-Match'] = cached.etag;
  }
  if (forceRefresh) {
    headers['Cache-Control'] = 'no-cache';
  }

  const res = await fetch(resolveUrl(`/api/v1/ref/${name}`), {
    method: 'GET',
    headers,
    signal,
  });

  // 304 — serve from in-memory cache
  if (res.status === 304) {
    if (cached) {
      return cached.body;
    }
    // Defensive: if we somehow got 304 with no cache, fall through to a fresh
    // fetch — this shouldn't happen in practice.
  }

  if (!res.ok) {
    throw new Error(`fetchCatalog(${name}): HTTP ${res.status}`);
  }

  const raw: unknown = await res.json();
  const parsed = refCatalogSchema.safeParse(raw);
  if (!parsed.success) {
    throw new RefDataSchemaError(name, parsed.error);
  }

  // Update the in-memory cache
  const etag = res.headers.get('ETag') ?? parsed.data.etag;
  _etagCache.set(name, { etag, body: parsed.data });

  return parsed.data;
}
